// Parallax para móvil y tablet (incl. iPhone e iPad).
//
// En escritorio el efecto "barrido" se consigue con `background-attachment: fixed`.
// En Safari de iOS y en Chrome de Android ese modo no funciona, así que aquí lo
// reproducimos: con `background-attachment: scroll` recalculamos la posición del
// fondo en cada scroll para que parezca anclado a la ventana, igual que el
// `fixed` de escritorio. En escritorio no hace nada.
//
// El tamaño del fondo se calcula una sola vez (y al girar/redimensionar), NO en
// cada scroll: así evitamos saltos en iOS cuando la barra de direcciones se
// oculta/muestra y cambia la altura de la ventana.
//
// Devuelve una función de limpieza para usar en el `return` de useEffect.
export function initParallax(selector) {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  if (!window.matchMedia("(hover: none) and (pointer: coarse)").matches) {
    return () => {};
  }

  const els = Array.from(document.querySelectorAll(selector));
  if (!els.length) return () => {};

  const layers = els.map((el) => {
    el.style.backgroundAttachment = "scroll";
    el.style.backgroundRepeat = "no-repeat";
    return { el, ratio: 0 };
  });

  layers.forEach((layer) => {
    const bg = window.getComputedStyle(layer.el).backgroundImage;
    const match = bg && bg.match(/url\(["']?(.*?)["']?\)/);
    if (!match || !match[1]) return;
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        layer.ratio = img.naturalWidth / img.naturalHeight;
        computeSizes();
        updatePositions();
      }
    };
    img.src = match[1];
  });

  // Tamaño "cover" respecto a la VENTANA (como un fondo fixed). Solo al cargar
  // la imagen y al redimensionar/girar.
  function computeSizes() {
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    for (const layer of layers) {
      if (!layer.ratio) continue;
      let w = vw;
      let h = vw / layer.ratio;
      if (h < vh) {
        h = vh;
        w = vh * layer.ratio;
      }
      layer.el.style.backgroundSize = Math.ceil(w) + "px " + Math.ceil(h) + "px";
    }
  }

  let ticking = false;
  function updatePositions() {
    ticking = false;
    for (const { el, ratio } of layers) {
      if (!ratio) continue;
      // Anclamos la imagen a la esquina superior izquierda de la ventana: al
      // restar la posición en pantalla del elemento, el fondo "no se mueve"
      // mientras la banda se desliza -> mismo barrido que en escritorio.
      const rect = el.getBoundingClientRect();
      el.style.backgroundPosition =
        Math.round(-rect.left) + "px " + Math.round(-rect.top) + "px";
    }
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(updatePositions);
    }
  }
  function onResize() {
    computeSizes();
    updatePositions();
  }

  computeSizes();
  updatePositions();
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onResize, { passive: true });

  return () => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onResize);
  };
}
